<!-- ProviderComparisonModal.vue -->
<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">
            <i class="mdi mdi-compare me-2"></i>Kenya Network Service Providers Comparison
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Provider Summary Cards -->
          <div class="row mb-4">
            <div class="col-md-2" v-for="provider in providers" :key="provider.name">
              <div class="card border h-100" :style="`border-left: 4px solid ${getProviderColor(provider.name)}!important`">
                <div class="card-body text-center p-3">
                  <div class="mb-2">
                    <img :src="getProviderLogo(provider.name)" width="40" height="40" class="rounded" :alt="provider.name">
                  </div>
                  <h6 class="card-title mb-1" style="font-size: 0.9rem;">{{ provider.name }}</h6>
                  <div class="text-muted small">
                    <div><strong>{{ provider.totalPoints }}</strong> points</div>
                    <div>{{ provider.avgSpeed }} Mbps avg</div>
                    <div v-if="provider.avgCost > 0">KES {{ provider.avgCost }}/mo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Detailed Comparison Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">Detailed Provider Comparison</h6>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead class="table-light">
                      <tr>
                        <th style="width: 200px;">Provider</th>
                        <th class="text-center">Coverage Points</th>
                        <th class="text-center">Avg Speed</th>
                        <th class="text-center">Avg Cost</th>
                        <th class="text-center">Reliability</th>
                        <th class="text-center">Connection Types</th>
                        <th class="text-center">Regions</th>
                        <th class="text-center">Market Share</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="provider in sortedProviders" :key="provider.name">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="provider-color-indicator me-2" :style="`background-color: ${getProviderColor(provider.name)}`"></div>
                            <img :src="getProviderLogo(provider.name)" width="30" height="30" class="rounded me-2" :alt="provider.name">
                            <div>
                              <strong>{{ provider.name }}</strong>
                              <div class="text-muted small">{{ getProviderDescription(provider.name) }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-info">{{ provider.totalPoints }}</span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <div class="progress me-2" style="width: 60px; height: 8px;">
                              <div class="progress-bar bg-success"
                                   :style="`width: ${Math.min(100, (provider.avgSpeed / 1000) * 100)}%`"></div>
                            </div>
                            <span class="small">{{ provider.avgSpeed }} Mbps</span>
                          </div>
                        </td>
                        <td class="text-center">
                            <span v-if="provider.avgCost > 0" class="text-success">
                              KES {{ numberFormat(provider.avgCost) }}
                            </span>
                          <span v-else class="text-muted">N/A</span>
                        </td>
                        <td class="text-center">
                          <div v-if="provider.avgReliability > 0" class="d-flex align-items-center justify-content-center">
                            <div class="progress me-2" style="width: 50px; height: 8px;">
                              <div class="progress-bar"
                                   :class="`bg-${getReliabilityColor(provider.avgReliability)}`"
                                   :style="`width: ${provider.avgReliability}%`"></div>
                            </div>
                            <span class="small">{{ provider.avgReliability }}%</span>
                          </div>
                          <span v-else class="text-muted">N/A</span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex flex-wrap justify-content-center gap-1">
                              <span v-for="(count, type) in provider.connectionTypes" :key="type"
                                    class="badge badge-outline"
                                    :class="`badge-outline-${getConnectionTypeColor(type)}`">
                                {{ type }} ({{ count }})
                              </span>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-secondary">{{ Object.keys(provider.regions).length }}</span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <div class="progress me-2" style="width: 50px; height: 8px;">
                              <div class="progress-bar bg-primary"
                                   :style="`width: ${getMarketSharePercentage(provider)}%`"></div>
                            </div>
                            <span class="small">{{ getMarketSharePercentage(provider) }}%</span>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Charts Section -->
          <div class="row mt-4">
            <!-- Market Share Chart -->
            <div class="col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-header">
                  <h6 class="mb-0">Market Share by Coverage Points</h6>
                </div>
                <div class="card-body">
                  <canvas id="marketShareChart" height="250"></canvas>
                </div>
              </div>
            </div>

            <!-- Speed Comparison Chart -->
            <div class="col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-header">
                  <h6 class="mb-0">Average Speed Comparison</h6>
                </div>
                <div class="card-body">
                  <canvas id="speedComparisonChart" height="250"></canvas>
                </div>
              </div>
            </div>
          </div>

          <!-- Cost vs Speed Analysis -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">Cost vs Speed Analysis</h6>
                </div>
                <div class="card-body">
                  <canvas id="costSpeedChart" height="300"></canvas>
                </div>
              </div>
            </div>
          </div>

          <!-- Provider Strengths Analysis -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">Provider Strengths & Specializations</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4" v-for="provider in topProviders" :key="provider.name">
                      <div class="border rounded p-3 mb-3 h-100">
                        <div class="d-flex align-items-center mb-2">
                          <img :src="getProviderLogo(provider.name)" width="25" height="25" class="rounded me-2">
                          <strong>{{ provider.name }}</strong>
                        </div>
                        <div class="mb-2">
                          <strong>Strongest in:</strong>
                          <div class="mt-1">
                            <span class="badge bg-success me-1" v-if="provider.avgSpeed > 100">High Speed</span>
                            <span class="badge bg-info me-1" v-if="provider.totalPoints > 10">Wide Coverage</span>
                            <span class="badge bg-warning me-1" v-if="provider.avgCost < 5000 && provider.avgCost > 0">Affordable</span>
                            <span class="badge bg-primary me-1" v-if="provider.avgReliability > 90">Reliable</span>
                          </div>
                        </div>
                        <div>
                          <strong>Primary Technology:</strong>
                          <span class="text-muted">{{ getPrimaryTechnology(provider) }}</span>
                        </div>
                        <div>
                          <strong>Coverage:</strong>
                          <span class="text-muted">{{ Object.keys(provider.regions).length }} regions</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" @click="downloadComparison">
            <i class="mdi mdi-download me-1"></i> Download Report
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
import Chart from 'chart.js/auto';

export default {
  name: 'ProviderComparisonModal',
  props: {
    show: { type: Boolean, default: false },
    providers: { type: Array, default: () => [] }
  },
  data() {
    return {
      marketShareChart: null,
      speedComparisonChart: null,
      costSpeedChart: null,
      providerColors: {
        'Safaricom': '#00AA44',
        'Zuku (Wananchi Group)': '#FF6600',
        'Airtel Kenya': '#FF0000',
        'Telkom Kenya': '#0066CC',
        'JTL Faiba': '#8B008B',
        'Poa Internet': '#00CCCC',
        'Liquid Telecom': '#FF69B4',
        'Mawingu Networks': '#32CD32'
      },
      providerDescriptions: {
        'Safaricom': 'Market leader with nationwide coverage',
        'Zuku (Wananchi Group)': 'Cable TV & fiber specialist',
        'Airtel Kenya': 'International telecom with 4G focus',
        'Telkom Kenya': 'Government-owned with competitive rates',
        'JTL Faiba': 'High-speed fiber specialist',
        'Poa Internet': 'Community-focused affordable WiFi',
        'Liquid Telecom': 'Enterprise & business solutions',
        'Mawingu Networks': 'Rural & underserved area specialist'
      }
    }
  },
  computed: {
    sortedProviders() {
      return [...this.providers].sort((a, b) => b.totalPoints - a.totalPoints);
    },
    topProviders() {
      return this.sortedProviders.slice(0, 3);
    },
    totalPoints() {
      return this.providers.reduce((sum, p) => sum + p.totalPoints, 0);
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.createCharts();
        });
      }
    }
  },
  methods: {
    createCharts() {
      this.createMarketShareChart();
      this.createSpeedComparisonChart();
      this.createCostSpeedChart();
    },

    createMarketShareChart() {
      const ctx = document.getElementById('marketShareChart');
      if (ctx) {
        if (this.marketShareChart) {
          this.marketShareChart.destroy();
        }

        const data = this.providers.map(p => p.totalPoints);
        const labels = this.providers.map(p => p.name);
        const colors = this.providers.map(p => this.getProviderColor(p.name));

        this.marketShareChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom', labels: { usePointStyle: true } },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const percentage = ((context.parsed / this.totalPoints) * 100).toFixed(1);
                    return `${context.label}: ${context.parsed} points (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
      }
    },

    createSpeedComparisonChart() {
      const ctx = document.getElementById('speedComparisonChart');
      if (ctx) {
        if (this.speedComparisonChart) {
          this.speedComparisonChart.destroy();
        }

        const labels = this.providers.map(p => p.name);
        const speeds = this.providers.map(p => parseFloat(p.avgSpeed));
        const colors = this.providers.map(p => this.getProviderColor(p.name));

        this.speedComparisonChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Average Speed (Mbps)',
              data: speeds,
              backgroundColor: colors.map(c => c + '80'),
              borderColor: colors,
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Speed (Mbps)' }
              },
              x: {
                ticks: { maxRotation: 45 }
              }
            },
            plugins: {
              legend: { display: false }
            }
          }
        });
      }
    },

    createCostSpeedChart() {
      const ctx = document.getElementById('costSpeedChart');
      if (ctx) {
        if (this.costSpeedChart) {
          this.costSpeedChart.destroy();
        }

        const data = this.providers.filter(p => p.avgCost > 0).map(p => ({
          x: parseFloat(p.avgCost),
          y: parseFloat(p.avgSpeed),
          label: p.name,
          color: this.getProviderColor(p.name)
        }));

        this.costSpeedChart = new Chart(ctx, {
          type: 'scatter',
          data: {
            datasets: [{
              label: 'Providers',
              data: data,
              backgroundColor: data.map(d => d.color + '80'),
              borderColor: data.map(d => d.color),
              borderWidth: 2,
              pointRadius: 8,
              pointHoverRadius: 10
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                title: { display: true, text: 'Average Cost (KES/month)' },
                beginAtZero: true
              },
              y: {
                title: { display: true, text: 'Average Speed (Mbps)' },
                beginAtZero: true
              }
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const point = context.raw;
                    return `${point.label}: KES ${point.x}/mo, ${point.y} Mbps`;
                  }
                }
              }
            }
          }
        });
      }
    },

    getProviderColor(name) {
      return this.providerColors[name] || '#666666';
    },

    getProviderLogo(name) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[name] || 'assets/images/providers/default-provider.png';
    },

    getProviderDescription(name) {
      return this.providerDescriptions[name] || 'Network service provider';
    },

    getConnectionTypeColor(type) {
      const colors = {
        'Fiber': 'primary',
        '4G': 'success',
        '5G': 'info',
        'Wireless': 'warning',
        'Satellite': 'secondary'
      };
      return colors[type] || 'primary';
    },

    getReliabilityColor(reliability) {
      if (reliability >= 95) return 'success';
      if (reliability >= 85) return 'warning';
      return 'danger';
    },

    getMarketSharePercentage(provider) {
      return ((provider.totalPoints / this.totalPoints) * 100).toFixed(1);
    },

    getPrimaryTechnology(provider) {
      if (!provider.connectionTypes) return 'N/A';

      const types = Object.entries(provider.connectionTypes);
      if (types.length === 0) return 'N/A';

      types.sort((a, b) => b[1] - a[1]);
      return types[0][0];
    },

    numberFormat(num) {
      return new Intl.NumberFormat().format(num);
    },

    downloadComparison() {
      // Generate comparison report
      this.$swal({
        icon: 'info',
        title: 'Generating Comparison Report',
        text: 'Your provider comparison report is being generated...',
        timer: 2000,
        showConfirmButton: false
      });

      setTimeout(() => {
        this.$swal({
          icon: 'success',
          title: 'Report Ready',
          text: 'Provider comparison report downloaded successfully!'
        });
      }, 2000);
    }
  },

  beforeDestroy() {
    if (this.marketShareChart) this.marketShareChart.destroy();
    if (this.speedComparisonChart) this.speedComparisonChart.destroy();
    if (this.costSpeedChart) this.costSpeedChart.destroy();
  }
}
</script>

<style scoped>
.modal.show { display: block; }
.modal-xl { max-width: 95%; }

.provider-color-indicator {
  width: 4px;
  height: 30px;
  border-radius: 2px;
}

.badge-outline-primary {
  color: #007bff;
  border: 1px solid #007bff;
  background: transparent;
}

.badge-outline-success {
  color: #28a745;
  border: 1px solid #28a745;
  background: transparent;
}

.badge-outline-info {
  color: #17a2b8;
  border: 1px solid #17a2b8;
  background: transparent;
}

.badge-outline-warning {
  color: #ffc107;
  border: 1px solid #ffc107;
  background: transparent;
}

.badge-outline-secondary {
  color: #6c757d;
  border: 1px solid #6c757d;
  background: transparent;
}

.progress {
  background-color: #e9ecef;
}

.table td {
  vertical-align: middle;
}

canvas {
  max-height: 300px;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}
</style>